import React, { useMemo } from "react"
import { Link } from "react-router-dom"
import { MenuItem, Stack, Typography } from "@mui/material"
import { FaIcon } from "../../../../mp-core/icons"
import Checkbox from "../Checkbox"
import { RuleName, useStyles } from "./styles"

type OptionHandler = (event?: React.MouseEvent<HTMLElement>) => void

interface DropdownMenuOptions {
  label?: string | React.ReactNode
  id?: string
  selected?: boolean
  icon?: string | React.ReactElement
  action?: OptionHandler
  href?: string
  divider?: boolean
  header?: boolean
  collapsibleHeader?: boolean
  children?: DropdownMenuOptions[]
  disabled?: boolean
  role?: string
  ["data-automation-id"]?: string
  ["aria-label"]?: string
}

export type { DropdownMenuOptions, OptionHandler }

type Props = DropdownMenuOptions & {
  classes?: Partial<Record<RuleName, string>>
  withCheckbox?: boolean
  component?: React.ElementType
  componentProps?: any
}

export const DropdownMenuItem: React.FC<Props> = ({
  classes: outerClasses,
  withCheckbox = false,
  selected = false,
  disabled = false,
  role,
  "data-automation-id": dataAutomationId,
  "aria-label": ariaLabel,
  action,
  href,
  icon,
  label,
  component,
  componentProps = {}
}) => {
  const { classes, cx } = useStyles()

  const isLink = !!href
  const isExternal = href?.startsWith("http") || href?.startsWith("//")

  const transformedComponentProps = useMemo(() => {
    let props = { ...componentProps }

    if (!!component) {
      props = {
        ...props,
        component
      }
    }

    if (isLink) {
      props = {
        ...props,
        component: isExternal ? "a" : Link,
        ...(isExternal ? { href } : { to: href })
      }
    }

    return props
  }, [href, isLink, isExternal, componentProps])

  return (
    <MenuItem
      tabIndex={0}
      className={cx(classes.menuItem, outerClasses?.menuItem, {
        [classes.menuItemWithCheckbox]: withCheckbox,
        [classes.menuItemSelected]: selected,
        ...(outerClasses?.menuItemWithCheckbox && {
          [outerClasses.menuItemWithCheckbox]: withCheckbox
        }),
        ...(outerClasses?.menuItemSelected && {
          [outerClasses.menuItemSelected]: selected
        })
      })}
      disabled={disabled}
      role={role}
      data-automation-id={dataAutomationId}
      aria-label={ariaLabel}
      disableRipple
      onClick={action}
      onKeyDown={e => {
        // Don't block Esc key
        if (e.key !== "Escape") {
          e.stopPropagation()
        }
      }}
      {...transformedComponentProps}
    >
      <Stack direction="row" spacing={3} alignItems="center">
        {withCheckbox && <Checkbox checked={selected} />}
        {typeof icon === "string" ? <FaIcon solid name={icon} /> : icon}
        <Typography variant="citation">{label}</Typography>
      </Stack>
    </MenuItem>
  )
}
